import { type FC, useRef, useEffect, useState, useTransition, useMemo, useCallback } from 'react';
import { graphql, useFragment, usePaginationFragment } from 'react-relay';
import {
    trackSponsoredItemsEvent,
    trackSponsoredLabelAbTestVariant,
} from 'dibs-sponsored-ads/exports/sponsoredItemsTracking.ts';
import { useIntl } from 'dibs-react-intl';

import { flattenConnectionNodes } from '../../utils/relayUtils.ts';
import { PdpRespSimilarItemsGrid } from './PdpRespSimilarItemsGrid.tsx';
import { youMayAlsoLike } from '../../shared/sharedMessages.jsx';
import {
    trackSimilarItemImpressions,
    trackSimilarItemClick,
} from '../tracking/similarItemsTracking.js';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

import classnames from 'classnames';
import styles from './PdpRespRefetchableSimilarItemsGrid.scss';
import dibsCss from 'dibs-css';

import { type Props } from './PdpRespSimilarItemsGrid.tsx';
import { type PdpRespRefetchableSimilarItemsGrid_viewer$key as Viewer } from './__generated__/PdpRespRefetchableSimilarItemsGrid_viewer.graphql.ts';
import { type PdpRespRefetchableSimilarItemsGrid_user$key as User } from './__generated__/PdpRespRefetchableSimilarItemsGrid_user.graphql.ts';
import { type PdpRespRefetchableSimilarItemsGrid_item$key as Item } from './__generated__/PdpRespRefetchableSimilarItemsGrid_item.graphql.ts';

import {
    MAXIMUM_YMAL_ITEMS_COUNT,
    SOLD_PDP_YMAL_ITEMS_PER_ROW,
    MORE_ML_ITEMS_FETCH_COUNT,
    SOLD_PDP_YMAL_SL_SUBSEQUENT,
    SOLD_PDP_YMAL_MAXIMUM_SIZE,
} from './pdpRespSimilarItemsConstants.ts';

import { usePdpSharedSimilarSponsoredItemsContext } from '../PdpSharedSponsoredItems/PdpSharedSimilarSponsoredItemsContext.tsx';
import {
    mergePdpSimilarItemsAndSponsoredItems,
    type PdpSimilarItemsMergedEntry,
} from '../PdpSharedSponsoredItems/mergePdpSimilarItemsAndSponsoredItems.ts';
import {
    canLoadPdpSimilarItemsOrganicPage,
    canLoadPdpSimilarItemsSponsoredPage,
    useVisiblePdpSimilarItems,
} from '../PdpSharedSponsoredItems/pdpSharedSimilarItemsScrollLoadDecision.ts';

const viewerFragment = graphql`
    fragment PdpRespRefetchableSimilarItemsGrid_viewer on Viewer
    @argumentDefinitions(
        first: { type: "Int", defaultValue: 10 }
        after: { type: "String", defaultValue: "" }
        itemId: { type: "String", defaultValue: "" }
    )
    @refetchable(queryName: "PdpRespRefetchableSimilarItemsGrid_viewerRefetchQuery") {
        ...PdpRespSimilarItemsGrid_viewer
        moreLikeThisItem(
            first: $first
            after: $after
            seller: EXCLUDE
            itemId: $itemId
            useML: true
            maxMLItems: 40
        ) @connection(key: "PdpRespRefetchableSimilarItemsGrid_moreLikeThisItem") {
            # edges used in flattenConnectionNodes
            # eslint-disable-next-line relay/unused-fields
            edges {
                node {
                    serviceId
                    title
                    browseUrl
                    categoryCode
                    ecommerceTrackingParams
                    ...PdpRespSimilarItemsGrid_similarItems
                    ...SharedRespRelatedItem_item @arguments(priceBookName: $priceBookName)
                    ...FavoritesProvider_item
                }
            }
            totalResults
            pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor
                endCursor
            }
        }
    }
`;
const itemFragment = graphql`
    fragment PdpRespRefetchableSimilarItemsGrid_item on Item {
        ...PdpRespSimilarItemsGrid_item
    }
`;
const userFragment = graphql`
    fragment PdpRespRefetchableSimilarItemsGrid_user on User {
        ...PdpRespSimilarItemsGrid_user
        ...SharedRespRelatedItem_user
        serviceId
    }
`;

type PdpRespRefetchableSimilarItemsGridProps = Omit<
    Props,
    'similarItems' | 'mergedEntries' | 'user' | 'viewer' | 'item'
> & {
    viewer: Viewer;
    user?: User | null;
    item: Item;
};

export const PdpRespRefetchableSimilarItemsGrid: FC<PdpRespRefetchableSimilarItemsGridProps> = ({
    viewer: viewerRef,
    user: userRef,
    item: itemRef,
    currency,
    favoritesLocation,
    gridLocation,
    dataTn,
    relayVariables,
    gridItemWrapperClass,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const { locale } = useIntl();
    const impressionTrackingRef = useRef<Record<string, boolean>>({});
    const {
        isEffectiveVariant,
        isSoldPdpSponsoredYmalGridVariant,
        gridSponsoredItems,
        metadataByItemId,
        sponsoredPagesLoaded,
        hasNextSponsored,
        isLoadingNextSponsored,
        hasSponsoredLoadError,
        inflateNextSponsored,
        isSponsoredListingsLoaded,
        gridSponsoredListingsIdsCount,
    } = usePdpSharedSimilarSponsoredItemsContext();

    const [shouldLoadUntilScrollDepthReached, setShouldLoadUntilScrollDepthReached] =
        useState(false);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [isSentinelVisible, setIsSentinelVisible] = useState(false);
    const [organicPagesLoaded, setOrganicPagesLoaded] = useState(0);
    const pairedLoadInFlightRef = useRef(false);
    const [isPairedLoadInFlight, setIsPairedLoadInFlight] = useState(false);
    const [, startTransition] = useTransition();

    const {
        data: viewer,
        loadNext,
        isLoadingNext,
        hasNext,
    } = usePaginationFragment(viewerFragment, viewerRef);

    const { totalResults } = viewer?.moreLikeThisItem || {};

    const organicItems = useMemo(
        () => flattenConnectionNodes(viewer?.moreLikeThisItem),
        [viewer?.moreLikeThisItem]
    );
    const maxItemsReached = organicItems.length >= MAXIMUM_YMAL_ITEMS_COUNT;
    const remainingOrganicCount = Math.max(0, MAXIMUM_YMAL_ITEMS_COUNT - organicItems.length);
    const { organicItems: visibleOrganicItems, sponsoredItems: visibleSponsoredItems } =
        useVisiblePdpSimilarItems({
            organicItems,
            sponsoredItems: gridSponsoredItems,
            organicPagesLoaded,
            sponsoredPagesLoaded,
            isEffectiveVariant,
            hasSponsoredLoadError,
            isLoadingNextSponsored,
            hasNext,
            hasNextSponsored,
        });

    const mergedEntries = useMemo((): PdpSimilarItemsMergedEntry<
        (typeof visibleOrganicItems)[number] | (typeof visibleSponsoredItems)[number]
    >[] => {
        const toOrganicEntries = (): PdpSimilarItemsMergedEntry<
            (typeof visibleOrganicItems)[number]
        >[] =>
            visibleOrganicItems.map(visibleOrganicItem => ({
                item: visibleOrganicItem,
                sponsoredMetadata: null,
            }));

        if (!isEffectiveVariant || visibleSponsoredItems.length === 0) {
            return toOrganicEntries();
        }

        return mergePdpSimilarItemsAndSponsoredItems({
            organicItems: visibleOrganicItems,
            sponsoredItems: visibleSponsoredItems,
            metadataByItemId,
        });
    }, [isEffectiveVariant, metadataByItemId, visibleOrganicItems, visibleSponsoredItems]);

    const items = useMemo(() => mergedEntries.map(entry => entry.item), [mergedEntries]);
    const maxDisplayCount = isEffectiveVariant
        ? SOLD_PDP_YMAL_MAXIMUM_SIZE
        : MAXIMUM_YMAL_ITEMS_COUNT;
    // The AB arm is only known after the client refetch, so SSR and the first hydrate pass must
    // render the same shimmer markup instead of committing to control's organic tiles.
    const isWaitingForInitialSponsoredMerge =
        !relayVariables.isClient ||
        (isSoldPdpSponsoredYmalGridVariant &&
            (!isSponsoredListingsLoaded || mergedEntries.length === 0));
    const isSubsequentPairedLoadInFlight =
        isEffectiveVariant && (isPairedLoadInFlight || isLoadingNext || isLoadingNextSponsored);
    const canRenderGridItems = !isWaitingForInitialSponsoredMerge;
    let renderedItemsCount = organicItems.length;
    if (!canRenderGridItems) {
        renderedItemsCount = 0;
    } else if (isEffectiveVariant) {
        renderedItemsCount = items.length;
    }

    const sponsoredListingsPlaceholderCount = isEffectiveVariant
        ? gridSponsoredListingsIdsCount
        : 0;
    const placeholderCeiling =
        totalResults && totalResults + sponsoredListingsPlaceholderCount < maxDisplayCount
            ? totalResults + sponsoredListingsPlaceholderCount
            : maxDisplayCount;
    const shimmerCount = placeholderCeiling - renderedItemsCount;
    const placeholdersCount = Math.max(
        Math.max(0, placeholderCeiling - renderedItemsCount),
        isWaitingForInitialSponsoredMerge || isSubsequentPairedLoadInFlight ? shimmerCount : 0
    );

    //The pagination driver for organic and sponsored listings
    useEffect(() => {
        //no sponsored pairing for control
        if (!isEffectiveVariant) {
            if (
                shouldLoadUntilScrollDepthReached &&
                !maxItemsReached &&
                !isLoadingNext &&
                hasNext &&
                remainingOrganicCount
            ) {
                loadNext(remainingOrganicCount);
            }
            return;
        }

        const shouldLoadOrganic = canLoadPdpSimilarItemsOrganicPage({
            hasNext,
            isLoadingNext,
            hasLoadError,
            organicItemsCount: organicItems.length,
        });
        const shouldLoadSponsored = canLoadPdpSimilarItemsSponsoredPage({
            isEffectiveVariant,
            hasNext,
            hasNextSponsored,
            isLoadingNextSponsored,
            hasSponsoredLoadError,
        });
        // do not start a paired fetch if last one still runs
        if (
            !isSentinelVisible ||
            pairedLoadInFlightRef.current ||
            (!shouldLoadOrganic && !shouldLoadSponsored)
        ) {
            return;
        }

        pairedLoadInFlightRef.current = true;
        setIsPairedLoadInFlight(true);

        const organicPromise = shouldLoadOrganic
            ? new Promise<void>((resolve, reject) => {
                  loadNext(Math.min(MORE_ML_ITEMS_FETCH_COUNT, remainingOrganicCount), {
                      onComplete: error => {
                          if (error) {
                              reject(error);
                          } else {
                              resolve();
                          }
                      },
                  });
              })
            : Promise.resolve();

        const sponsoredPromise = shouldLoadSponsored
            ? inflateNextSponsored(SOLD_PDP_YMAL_SL_SUBSEQUENT)
            : Promise.resolve();

        void Promise.allSettled([organicPromise, sponsoredPromise]).then(([organicResult]) => {
            startTransition(() => {
                if (shouldLoadOrganic) {
                    if (organicResult.status === 'fulfilled') {
                        setOrganicPagesLoaded(pages => pages + 1);
                    } else {
                        setHasLoadError(true);
                    }
                }
            });
            pairedLoadInFlightRef.current = false;
            setIsPairedLoadInFlight(false);
        });
    }, [
        isEffectiveVariant,
        organicItems.length,
        remainingOrganicCount,
        maxItemsReached,
        shouldLoadUntilScrollDepthReached,
        isSentinelVisible,
        hasNext,
        isLoadingNext,
        loadNext,
        hasLoadError,
        hasSponsoredLoadError,
        hasNextSponsored,
        isLoadingNextSponsored,
        inflateNextSponsored,
        startTransition,
    ]);

    function loadMore({ disconnect }: { disconnect?: () => void }): void {
        if (isLoadingNext || !hasNext || remainingOrganicCount <= 0) {
            if (maxItemsReached) {
                disconnect?.();
            }
            return;
        }

        loadNext(Math.min(MORE_ML_ITEMS_FETCH_COUNT, remainingOrganicCount));
    }

    function trackItemsImpression({ index }: { index: number }): void {
        const itemsToTrack = organicItems
            .slice(index, index + SOLD_PDP_YMAL_ITEMS_PER_ROW)
            .filter(i => (i?.serviceId ? !impressionTrackingRef.current[i.serviceId] : false))
            .map(i => ({ ...i, isSponsored: false }));

        itemsToTrack.forEach(i => {
            if (i?.serviceId) {
                impressionTrackingRef.current[i.serviceId] = true;
            }
        });

        if (itemsToTrack.length) {
            trackSimilarItemImpressions(itemsToTrack, gridLocation, index);
        }
    }

    const handleSponsoredEvent = useCallback(
        (index: number, eventType: 'click' | 'impression'): void => {
            const sponsoredEventLink =
                mergedEntries[index]?.sponsoredMetadata?.[`${eventType}TrackerLink`];
            if (sponsoredEventLink) {
                trackSponsoredItemsEvent(sponsoredEventLink);
                if (eventType === 'impression') {
                    trackSponsoredLabelAbTestVariant(locale);
                }
            }
        },
        [locale, mergedEntries]
    );

    const handleItemClick = useCallback(
        (index: number): void => {
            handleSponsoredEvent(index, 'click');
            const isSponsored = !!mergedEntries[index]?.sponsoredMetadata;
            trackSimilarItemClick(index, { ...items[index], isSponsored }, gridLocation);
        },
        [handleSponsoredEvent, mergedEntries, gridLocation, items]
    );

    const handleItemVisible = useCallback(
        (index: number): void => {
            const visibleItem = items[index];
            if (visibleItem?.serviceId && !impressionTrackingRef.current[visibleItem.serviceId]) {
                impressionTrackingRef.current[visibleItem.serviceId] = true;
                handleSponsoredEvent(index, 'impression');
                const isSponsored = !!mergedEntries[index]?.sponsoredMetadata;
                trackSimilarItemImpressions([{ ...visibleItem, isSponsored }], gridLocation, index);
            }
        },
        [handleSponsoredEvent, items, gridLocation, mergedEntries]
    );

    const itemsPlaceholder =
        placeholdersCount > 0 ? (
            <div className="rowFlex">
                {Array.from({ length: placeholdersCount }).map((_, index) => (
                    <div
                        key={index}
                        data-tn={`similar-item-placeholder-${index}`}
                        className={classnames(
                            'colLg3 colMd6 colXs6',
                            styles.similarItem,
                            styles.placeholderItem,
                            dibsCss.pSmall
                        )}
                    >
                        <div className={classnames(styles.imagePlaceholder, dibsCss.mbSmaller)} />
                        <div className={classnames(styles.titlePlaceholder, dibsCss.mbXsmall)} />
                        <div className={styles.pricePlaceholder} />
                    </div>
                ))}
            </div>
        ) : null;

    return (
        <div>
            <PdpRespSimilarItemsGrid
                isUnavailablePdp
                header={youMayAlsoLike}
                relayVariables={relayVariables}
                viewer={viewer}
                currency={currency}
                user={user}
                item={item}
                similarItems={canRenderGridItems ? items : []}
                mergedEntries={canRenderGridItems ? mergedEntries : []}
                placeholder={itemsPlaceholder}
                favoritesLocation={favoritesLocation}
                gridLocation={gridLocation}
                dataTn={dataTn}
                isDark={false}
                hideBar
                gridItemWrapperClass={gridItemWrapperClass}
                onRowImpression={isEffectiveVariant ? undefined : trackItemsImpression}
                onItemClick={isEffectiveVariant ? handleItemClick : undefined}
                onItemImpression={isEffectiveVariant ? handleItemVisible : undefined}
                onLastItemVisibilityChange={({ isVisible, boundingClientRect, disconnect }) => {
                    setShouldLoadUntilScrollDepthReached(
                        window && window.scrollY >= boundingClientRect.bottom
                    );

                    if (isVisible && !isEffectiveVariant) {
                        loadMore({ disconnect });
                    }

                    setIsSentinelVisible(isVisible);
                }}
            />
            {isEffectiveVariant && hasNext && (
                <VisibilityTracker
                    watchAfterFirstVisible
                    className={classnames(dibsCss.wFull, dibsCss.h1px)}
                    observerOptions={{ rootMargin: '40%' }}
                    onVisibilityChange={({ isVisible }) => setIsSentinelVisible(isVisible)}
                />
            )}
        </div>
    );
};
